<template>
  <article>
    <ul>
      <li
        v-for="(audioWork, index) in annotatedAudioWorks"
        :key="index"
        class="text-sm flex items-center sm:grid grid-cols-2"
        :class="{ 'border-b md:border-b-2 border-app-primary-grey--concrete pb-6 mb-6': index + 1 < annotatedAudioWorks.length }"
      >
        <div class="flex-auto">
          <h4 v-if="audioWork.composers && audioWork.composers.length" class="font-semibold mb-1">
            {{ audioWork.composers.map((composer: CmsData) => composer.title).join(' / ') }}
          </h4>
          <p v-if="audioWork.parent && audioWork.parent.id" class="text-sm">
            {{ t_('part-title', { part: audioWork.title, work: audioWork.parent.title }) }} {{ audioWork.extraInfo }}
            <template v-if="audioWork.extraLines.length > 0">({{ audioWork.extraLines.join(', ') }})</template>
          </p>
          <p v-else class="text-sm">
            {{ audioWork.title }}
            <template v-if="audioWork.extraLines.length > 0">({{ audioWork.extraLines.join(', ') }})</template>
          </p>
        </div>
        <div v-if="audioWork.audio" class="flex pl-4 shrink-0 justify-end sm:justify-start">
          <audio :id="audioWork.id">
            <source :src="audioWork.audio.url" :type="audioWork.audio.mimeType" />
          </audio>
          <AtomIcon
            :icon="playerStates[audioWork.id] ? 'app-pause' : 'app-play'"
            classes="hover:text-app-primary-1-orange--bittersweet cursor-pointer transform transition duration-200 w-10 shrink-0"
            @click="togglePlayerState(audioWork.id)"
          />
        </div>
      </li>
    </ul>
  </article>
</template>

<script setup lang="ts">
const props = defineProps({
  audioWorks: { type: Array as PropType<any[]>, required: true },
})

const { t_ } = setupTranslationPrefixer('AudioWorks')
const gtmCdpAction = setupGtmCdpAction()
const annotatedAudioWorks = computed(() =>
  props.audioWorks.map((audioWork) => ({
    ...audioWork,
    extraLines: [audioWork.extraInfo, audioWork.encore ? t_('has-encore') : false].filter((value) => !!value),
  })),
)
const playerStates = ref(Object.fromEntries(props.audioWorks.map((audioWork) => [audioWork.id, false])))
const hasAudioFragments = annotatedAudioWorks.value.some(({ audio }) => audio)

if (hasAudioFragments) {
  setupGtmCdpEvent('CV07 - Music fragment available')
}

const setPlayerState = (id: string, state: boolean, rewind?: boolean) => {
  playerStates.value[id] = state
  const player = document.getElementById(id) as HTMLMediaElement | null
  if (player) {
    if (state) {
      if (rewind) player.currentTime = 0
      player.play()
      gtmCdpAction('CV07 - Works music fragment clicked')
    } else {
      player.pause()
      if (rewind) player.currentTime = 0
      gtmCdpAction('CV07 - Music fragment paused')
    }
  }
}
const togglePlayerState = (id: string) => {
  // Check if we only need to pause
  if (playerStates.value[id]) {
    setPlayerState(id, false)
  } else {
    // Ensure that no other players are playing, rewind any that were playing
    for (const [id_, state] of Object.entries(playerStates.value)) {
      if (state) {
        setPlayerState(id_, false, true)
      }
    }
    // Start the selected player, when this was previously playing it will not rewind
    setPlayerState(id, true)
  }
}
</script>
